import { Building2, GraduationCap, Layers, Presentation, type LucideIcon } from 'lucide-react';

/**
 * One row of any of the four L&D vocabularies.
 *
 * **Not `PersonnelTraining`**, which is one employee having attended one thing. This is the
 * vocabulary that record is described with, maintained under Personnel → Settings with its own
 * `learning-development.*` keys.
 *
 * **Every nullable field is optional, not `| null`.** The API sets
 * `JsonIgnoreCondition.WhenWritingNull`, so a null property is not sent at all — it arrives as
 * `undefined`. Check falsiness, never identity.
 */
export interface LearningDevelopmentEntry {
  id: string;
  name: string;
  description?: string | null;

  /**
   * Where it sits in the vocabulary's own order.
   *
   * The STORED value, gapped in tens — not a position counted from one. A page of records
   * cannot compute a position, which is why the column is headed "Order" and the pickers do the
   * ordering instead.
   */
  sortOrder: number;

  /**
   * Live L&D records filed under it. Derived on every read, never stored — and it is what the
   * delete confirmation counts, so a reader is told what retiring it would leave behind.
   */
  trainingCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/**
 * What the modal sends.
 *
 * **No `sortOrder`.** A position is assigned, never asked for: the server appends a new row in a
 * gap of ten and leaves an existing row's order alone, so a rename can never become a reorder.
 */
export interface LearningDevelopmentRequest {
  name: string;
  description?: string | null;
}

/** One vocabulary row as the Training/L&D form's pickers need it. */
export interface LearningDevelopmentOption {
  id: string;
  name: string;
}

/** The route slug of one vocabulary — the segment the API and the SPA both address it by. */
export type LearningDevelopmentCatalogSlug =
  | 'types'
  | 'categories'
  | 'interventions'
  | 'sponsors';

/**
 * Everything one vocabulary's pages differ by.
 *
 * **Four vocabularies, one set of screens.** They are one shape — name, description, position,
 * a count of the records filed under them — so four copies of an index page and four of a
 * profile page would be eight files whose sixth divergence nobody notices. This descriptor is
 * what the one index page and the one profile page are parameterised by, which is the same
 * thing `<SettingsMenu>` and `<IndexPage>` already are: configuration-driven shells, not a new
 * pattern.
 *
 * What they share is not a coincidence of implementation. It is the reason they share ONE
 * permission module: maintaining these four is one administrative job.
 */
export interface LearningDevelopmentCatalog {
  slug: LearningDevelopmentCatalogSlug;

  /** The heading, the breadcrumb leaf, and the Settings row — "L&D Types". */
  label: string;

  /** What one row IS, for a button, a dialog title and a message — "L&D type". */
  noun: string;

  /** The plural of `noun`, for an empty state and a count. */
  nounPlural: string;

  /** One line on the Settings menu row, saying what it configures. */
  description: string;

  /** The word above the name column, which is the row's anchor. */
  columnHeader: string;

  /** What the form asks under the name field. */
  fieldDescription: string;

  /** The record mark on the profile header, and the Settings menu row's icon. */
  icon: LucideIcon;

  /** Where the form that picks from it says a new row is maintained. */
  settingsPath: string;
}

/**
 * The four, in the order the L&D form asks them.
 *
 * Ordered by the FORM rather than alphabetically, so the Settings menu reads the way the modal
 * does — a reader who has just filled one finds the vocabularies in the order they met them.
 */
export const LEARNING_DEVELOPMENT_CATALOGS: readonly LearningDevelopmentCatalog[] = [
  {
    slug: 'types',
    label: 'L&D Types',
    noun: 'L&D type',
    nounPlural: 'L&D types',
    description:
      'What kind of learning or development a record is — a training, academic study, professional development, on-the-job development, self-development, a developmental activity.',
    columnHeader: 'L&D type',
    fieldDescription:
      'What kind of learning or development this is, as the L&D form offers it.',
    icon: GraduationCap,
    settingsPath: '/personnel/settings/ld-types',
  },
  {
    slug: 'categories',
    label: 'L&D Categories',
    noun: 'L&D category',
    nounPlural: 'L&D categories',
    /*
      The description says outright that this IS the CSC column, because the form prints
      different words: an administrator looking for "Managerial" needs to know it is asked here
      as Leadership & Management rather than adding a second row for it.
    */
    description:
      "What an L&D record was about — the CSC form's Type of LD. Managerial and Supervisory are asked here as Leadership & Management.",
    columnHeader: 'L&D category',
    fieldDescription: "What an L&D record is about — the CSC form's Type of LD.",
    icon: Layers,
    settingsPath: '/personnel/settings/ld-categories',
  },
  {
    slug: 'interventions',
    label: 'Development Interventions',
    noun: 'development intervention',
    nounPlural: 'development interventions',
    description:
      'How an L&D record was delivered — the modality, asked of every record rather than only of a developmental activity.',
    columnHeader: 'Intervention',
    fieldDescription: 'How an L&D record was delivered — classroom, on the job, self-directed.',
    icon: Presentation,
    settingsPath: '/personnel/settings/development-interventions',
  },
  {
    slug: 'sponsors',
    label: 'Conducted / Sponsored By',
    noun: 'sponsor',
    nounPlural: 'sponsors',
    /*
      The only one of the four that ships empty, and the description says so: there is no
      national vocabulary of sponsors, so this list is the employer's own and fills itself from
      the L&D form.
    */
    description:
      "The institutions and agencies that conduct or sponsor L&D. This list is this employer's own — it starts empty and fills from the L&D form.",
    columnHeader: 'Sponsor',
    fieldDescription: 'The institution or agency, written as it appears on a certificate.',
    icon: Building2,
    settingsPath: '/personnel/settings/training-sponsors',
  },
];

/** The descriptor for one slug, or undefined for a slug this product does not have. */
export function learningDevelopmentCatalog(
  slug: string,
): LearningDevelopmentCatalog | undefined {
  return LEARNING_DEVELOPMENT_CATALOGS.find((c) => c.slug === slug);
}
